import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { JavapiService } from 'src/app/services/javapi.service';
import { MetodosgenService } from 'src/app/services/metodosgen.service';
import { Metodosui } from 'src/app/services/metodosui';

export interface GaleriaItemIF {
  id: string;
  clasificacion: string;
  imagen_path: string;
  texto: string;
  fecha: string;
  tipo: string;
  source: string;
  selectedAs?: 'ANTES' | 'DESPUES' | null;
}

@Component({
  selector: 'app-informe-fotografico',
  templateUrl: './informe-fotografico.component.html',
  styleUrls: ['./informe-fotografico.component.scss'],
  standalone: false
})
export class InformeFotograficoComponent implements OnInit {

  @Input() trabajo: any;
  @Input() clienteNombre: string = '';
  @Input() cotizacionId: number | null = null;

  /** Segmento activo de visualización */
  segment: 'ANTES' | 'DESPUES' = 'ANTES';
  
  /** Datos del Formulario Dinámico */
  ubicacion: string = '';
  cotizacionNum: string = '';
  otAsignada: string = '';
  fechaInforme: string = '';
  usuarioActual: string = 'system';

  /** Todas las imágenes de la galería */
  allImages: GaleriaItemIF[] = [];

  /** Imágenes filtradas por segmento activo */
  filteredImages: GaleriaItemIF[] = [];

  /** Imágenes seleccionadas ANTES */
  selectedAntes: GaleriaItemIF[] = [];

  /** Imágenes seleccionadas DESPUES */
  selectedDespues: GaleriaItemIF[] = [];

  isLoading: boolean = false;
  isGenerating: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    public javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui
  ) { }

  ifEditorState: any = null;

  async ngOnInit() {
    this.ubicacion = this.trabajo?.lugar || '';
    this.fechaInforme = new Date().toISOString().split('T')[0];

    const userJson = localStorage.getItem('user');
    if (userJson) {
      this.usuarioActual = JSON.parse(userJson).l_usuario || 'system';
    }

    // Cargar cotización y OT vinculadas al trabajo
    if (this.cotizacionId) {
      console.log('Inicializando cotización de entrada:', this.cotizacionId);
      this.cotizacionNum = String(this.cotizacionId);
    } else {
      console.log('Buscando cotización vinculada vía API para trabajo:', this.trabajo?.id);
      await this.loadCotizacionData();
    }
    await this.loadIfEditorJson();
    this.loadGaleria();
  }

  async loadIfEditorJson() {
    try {
      const payload = this.metodosGenService.setModeloGeneral(
        "genModel", "getArsInfo",
        String(this.trabajo.id),
        '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      );
      const res: any = await firstValueFrom(this.javapiService.getCommonModel(payload));
      if (res && res.length > 0 && res[0].dt6) {
        this.ifEditorState = JSON.parse(res[0].dt6);
      } else {
        this.ifEditorState = { images: {} };
      }
    } catch (e) {
      console.error('Error loading if_editor_json', e);
      this.ifEditorState = { images: {} };
    }
  }

  /** Consulta la cotización vinculada al trabajo para obtener su ID y OT asignada */
  private async loadCotizacionData() {
    if (!this.trabajo?.id) return;
    try {
      const res: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            'cotizaciones', 'cotizacionesT',
            String(this.trabajo.id),
            '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      console.log('Resultado de loadCotizacionData:', res);
      if (Array.isArray(res) && res.length > 0) {
        // dt1 = c_id (N° Cotización). OT no viene en este SP → queda en blanco para ingreso manual.
        this.cotizacionNum = res[0].dt1 ? String(res[0].dt1) : '';
        this.otAsignada   = ''; // Sin fuente disponible en SP; el usuario puede editarlo
      }
    } catch (e) {
      console.error('Error cargando datos de cotización para el informe', e);
    }
  }

  async loadGaleria() {
    this.isLoading = true;
    try {
      const res: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            'genModel', 'trabajosGaleria',
            String(this.trabajo.id),
            '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );

      if (Array.isArray(res)) {
        this.allImages = res
          .filter(d => d.dt3 && d.dt3.length > 5) // Solo imágenes, no notas
          .map(d => {
            const originalPath = d.dt3;
            let currentPath = originalPath;
            let source = d.dt15 || 'galeria';

            // Si hay un estado editado guardado, usar la versión editada
            if (this.ifEditorState && this.ifEditorState.images && this.ifEditorState.images[originalPath]) {
              const savedState = this.ifEditorState.images[originalPath];
              if (savedState.editedPath) {
                currentPath = savedState.editedPath;
                source = 'if_edited';
              }
            }

            return {
              id: d.dt1,
              clasificacion: d.dt2,
              imagen_path: currentPath,
              texto: d.dt4,
              fecha: d.dt5,
              tipo: 'IMAGEN',
              source: source,
              selectedAs: null
            };
          });
        this.filterImages();
      }
    } catch (e) {
      console.error('Error loading galería para informe', e);
    } finally {
      this.isLoading = false;
    }
  }

  filterImages() {
    this.filteredImages = this.allImages.filter(img => img.clasificacion === this.segment);
  }

  segmentChanged(ev: any) {
    this.segment = ev.detail.value;
    this.filterImages();
  }

  getImagePath(source: string, path: string = ''): string {
    if (source === 'if_edited' || (path && path.startsWith('img_json/'))) {
      return 'path_doc_if';
    }
    switch (source) {
      case 'inspeccion': return 'path_img_inspecciones';
      case 'medida': return 'path_img_medidas';
      default: return 'path_img_trabajos';
    }
  }

  isSelected(item: GaleriaItemIF): boolean {
    return item.selectedAs === this.segment;
  }

  toggleSelect(item: GaleriaItemIF) {
    if (item.selectedAs === this.segment) {
      // Deseleccionar
      item.selectedAs = null;
    } else {
      item.selectedAs = this.segment;
    }
    this.rebuildSelections();
  }

  private rebuildSelections() {
    this.selectedAntes = this.allImages.filter(img => img.selectedAs === 'ANTES');
    this.selectedDespues = this.allImages.filter(img => img.selectedAs === 'DESPUES');
  }

  get totalSelected(): number {
    return this.selectedAntes.length + this.selectedDespues.length;
  }


  async generarInforme() {
    if (this.selectedAntes.length === 0 && this.selectedDespues.length === 0) {
      this.metodosUi.showToast('Selecciona al menos una imagen para el informe.', 'warning');
      return;
    }

    if (!this.cotizacionNum || this.cotizacionNum.trim().length === 0) {
      this.metodosUi.showToast('El N° de Cotización es obligatorio para generar el informe.', 'warning');
      return;
    }

    this.isGenerating = true;
    try {
      const payload = {
        trabajoId: String(this.trabajo.id),
        ubicacion: this.ubicacion,
        cotizacionNum: this.cotizacionNum,
        otAsignada: this.otAsignada,
        fecha: this.fechaInforme,
        antesImages: this.selectedAntes.map(img => ({
          imagenPath: img.imagen_path,
          texto: img.texto || '',
          source: img.source || 'galeria'
        })),
        despuesImages: this.selectedDespues.map(img => ({
          imagenPath: img.imagen_path,
          texto: img.texto || '',
          source: img.source || 'galeria'
        })),
        usuario: this.usuarioActual
      };

      const res: any = await firstValueFrom(this.javapiService.generarInformeFotografico(payload));
      
      if (res && res.success) {
        this.metodosUi.showToast('Informe Fotográfico generado exitosamente.', 'success');
        // Cerrar modal pasando la URL del PDF para que el padre la gestione
        const pdfUrl = this.javapiService.getFileUrl('INF', this.trabajo.id);
        this.modalCtrl.dismiss({ success: true, pdfUrl, cotizacionNum: this.cotizacionNum });
      } else {
        this.metodosUi.showToast(res?.error || 'Error al generar el informe fotográfico.', 'danger');
      }
    } catch (e: any) {
      console.error('Error al generar informe fotográfico', e);
      this.metodosUi.showToast('Error de comunicación con el servidor.', 'danger');
    } finally {
      this.isGenerating = false;
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
